import { THEME_SCOPE_PROP } from './internal-props';
export function mergeChildren(prevChildren, nextChildrenRaw, options) {
    const { ensureNodeId, getComparableType, shouldTrackProps, resolveStaticProps } = options;
    const nextChildren = nextChildrenRaw.map((child) => ensureNodeId(child));
    if (prevChildren.length === 0) {
        if (nextChildren.length === 0) {
            return { children: prevChildren, needsPostMountDiff: false };
        }
        return { children: prevChildren, needsPostMountDiff: true };
    }
    if (prevChildren.length !== nextChildren.length) {
        return { children: prevChildren, needsPostMountDiff: true };
    }
    const prevByKey = new Map();
    for (const child of prevChildren) {
        const explicitKey = getExplicitKey(child);
        if (explicitKey !== null) {
            prevByKey.set(explicitKey, child);
        }
    }
    for (let index = 0; index < nextChildren.length; index += 1) {
        const next = nextChildren[index];
        const explicitKey = getExplicitKey(next);
        if (explicitKey !== null) {
            const prev = prevByKey.get(explicitKey);
            if (!prev || getComparableType(prev) !== getComparableType(next)) {
                return { children: prevChildren, needsPostMountDiff: true };
            }
            if (prevChildren[index].id !== prev.id) {
                return { children: prevChildren, needsPostMountDiff: true };
            }
            if (hasStaticDiff(prev, next, shouldTrackProps, resolveStaticProps)) {
                return { children: prevChildren, needsPostMountDiff: true };
            }
            continue;
        }
        const prevAtIndex = prevChildren[index];
        if (!prevAtIndex || getComparableType(prevAtIndex) !== getComparableType(next)) {
            return { children: prevChildren, needsPostMountDiff: true };
        }
        if (getExplicitKey(prevAtIndex) !== null) {
            return { children: prevChildren, needsPostMountDiff: true };
        }
        if (hasStaticDiff(prevAtIndex, next, shouldTrackProps, resolveStaticProps)) {
            return { children: prevChildren, needsPostMountDiff: true };
        }
    }
    return { children: prevChildren, needsPostMountDiff: false };
}
export function diffPropObjects(prev, next, options) {
    const { shouldTrackProps, resolveStaticProps } = options;
    const patches = [];
    const prevProps = shouldTrackProps(prev) ? resolveStaticProps(prev.props) : {};
    const nextProps = shouldTrackProps(next) ? resolveStaticProps(next.props) : {};
    if (!Object.is(prev._theme, next._theme)) {
        if (next._theme) {
            patches.push({ op: 'set-prop', id: next.id, name: THEME_SCOPE_PROP, value: next._theme });
        }
        else if (prev._theme) {
            patches.push({ op: 'remove-prop', id: next.id, name: THEME_SCOPE_PROP });
        }
    }
    for (const [name, nextValue] of Object.entries(nextProps)) {
        if (!Object.prototype.hasOwnProperty.call(prevProps, name)) {
            patches.push({ op: 'set-prop', id: next.id, name, value: nextValue });
            continue;
        }
        const prevValue = prevProps[name];
        if (!Object.is(prevValue, nextValue)) {
            patches.push({ op: 'set-prop', id: next.id, name, value: nextValue });
        }
    }
    for (const name of Object.keys(prevProps)) {
        if (!Object.prototype.hasOwnProperty.call(nextProps, name)) {
            patches.push({ op: 'remove-prop', id: next.id, name });
        }
    }
    if (next.text !== prev.text && next.text !== undefined) {
        patches.push({ op: 'set-text', id: next.id, value: String(next.text) });
    }
    else if (prev.text !== undefined && next.text === undefined) {
        patches.push({ op: 'set-text', id: next.id, value: '' });
    }
    return patches;
}
export function longestIncreasingSubsequenceIndices(sequence) {
    const predecessors = new Array(sequence.length).fill(-1);
    const pileTops = [];
    const pileIndices = [];
    for (let index = 0; index < sequence.length; index += 1) {
        const value = sequence[index];
        if (value < 0) {
            continue;
        }
        let low = 0;
        let high = pileTops.length;
        while (low < high) {
            const mid = (low + high) >> 1;
            if (pileTops[mid] < value) {
                low = mid + 1;
            }
            else {
                high = mid;
            }
        }
        if (low > 0) {
            predecessors[index] = pileIndices[low - 1];
        }
        pileTops[low] = value;
        pileIndices[low] = index;
    }
    const stable = new Set();
    if (pileIndices.length === 0) {
        return stable;
    }
    let index = pileIndices[pileIndices.length - 1];
    while (index >= 0) {
        stable.add(index);
        index = predecessors[index];
    }
    return stable;
}
function getExplicitKey(node) {
    if (node.key === undefined || node.key === null) {
        return null;
    }
    return node.key;
}
function hasStaticDiff(prev, next, shouldTrackProps, resolveStaticProps) {
    if (prev.text !== next.text) {
        if (prev.text !== undefined || next.text !== undefined) {
            return true;
        }
    }
    if (!Object.is(prev._theme, next._theme)) {
        return true;
    }
    if (!shouldTrackProps(prev) || !shouldTrackProps(next)) {
        return false;
    }
    const prevProps = resolveStaticProps(prev.props);
    const nextProps = resolveStaticProps(next.props);
    for (const [name, value] of Object.entries(nextProps)) {
        if (!Object.prototype.hasOwnProperty.call(prevProps, name)) {
            return true;
        }
        if (!Object.is(prevProps[name], value)) {
            return true;
        }
    }
    for (const name of Object.keys(prevProps)) {
        if (!Object.prototype.hasOwnProperty.call(nextProps, name)) {
            return true;
        }
    }
    return false;
}
//# sourceMappingURL=reconcile.js.map